import Tippy from '@tippyjs/react';
import cogoToast from 'cogo-toast';
import moment from 'moment';
import numeral from 'numeral';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button } from "react-bootstrap";
import { useQuery } from "react-query";
import { PageTitle } from "../../../../../_metronic/layout/core";
import CardEx from '../../../../Components/Cards/CardEx';
import Semaphore from "../../../../Components/Semaphore/Semaphore";
import WrappedAgTable from "../../../../Components/Table/WrappedAgTable";
import { createCustomDateFilterParams } from '../../../../Components/Utils/Utils';
import { useGetUser } from "../../../../Hooks/CustomHooks";
import { GetDetalleGarantiasListFullWithFilter } from "../../../../api/Api";
import PermissionsGate from "../../../../api/PermissionsGate";
import { SCOPES } from "../../../../config/permissions-map";
import { Unauthorized } from "../../../Global/unauthorized";
import { CELL_CONSTANTS, EXCEPCION_GARANTIA_TIPO, GARANTIA_ESTADO, GARANTIA_TIPO } from "../../../Utils/Constants";
import EditarGarantiaModal from "./EditarGarantiaModal";
import AgregarBancoModal from "./AgregarBancoModal";
import '../RevisionGarantia/Garantias.css';

export default function ListadoGarantiasEdicion({ title = "Edición de Garantías" }) {
    const gridApiRef = useRef(null);
    const columnApiRef = useRef(null);
    const currentDate = moment();

    const [selectedGarantiaId, setSelectedGarantiaId] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddBankModal, setShowAddBankModal] = useState(false);

    const { data: data_revisiones,
        refetch: refetch_data_revisiones,
        isFetching: isFetchingDataRevisiones,
    } = useQuery("getDetalleGarantiasListToEdicion", () => GetDetalleGarantiasListFullWithFilter({ "tipo_garantia": null }));

    const handleOpenEdit = useCallback((id) => {
        setSelectedGarantiaId(id);
        setShowEditModal(true);
    }, []);

    const handleSuccessEdit = useCallback(() => {
        refetch_data_revisiones();
    }, [refetch_data_revisiones]);

    const breadcrumbs = [
        { title: "Inicio", path: "/dashboard", isActive: true, isSeparator: false },
        { title: "", path: "", isActive: false, isSeparator: true },
        { title: "Garantías", path: "", isActive: false, isSeparator: false },
        { title: "", path: "", isActive: false, isSeparator: true },
        { title: "Edición de Garantías", path: "/garantias/listado_garantias_edicion", isActive: false, isSeparator: false },
    ];

    const header = useMemo(() => [
        {
            headerName: 'Id',
            field: 'detalle_garantia.id_garantia_detalle',
            pinned: 'left',
            width: 110,
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'Semáforos',
            field: 'semaforo_locatario',
            pinned: 'left',
            filter: false,
            sortable: false,
            width: 120,
            cellRenderer: (props) => props.data ? (
                <div className="align-items-center d-flex h-100">
                    <Semaphore caption="LO" size={'25px'} fontSize={'11px'}
                        tooltipTitle={"Revisión de Locatario"}
                        lastUpdateDate={props.data.detalle_garantia?.revision_locatario?.fecha_revision}
                        lastUpdatedBy={props.data.detalle_garantia?.revision_locatario?.usuario_revisor}
                        tooltipDescription={props.data.detalle_garantia?.revision_locatario?.observaciones}
                        color={props.data.detalle_garantia.revision_locatario?.semaforo_color}>
                    </Semaphore>
                    <Semaphore caption="CO" size={'25px'} fontSize={'11px'}
                        tooltipTitle={"Revisión de Cobranza"}
                        lastUpdateDate={props.data.detalle_garantia?.revision_cobranza?.fecha_revision}
                        lastUpdatedBy={props.data.detalle_garantia?.revision_cobranza?.usuario_revisor}
                        tooltipDescription={props.data.detalle_garantia?.revision_cobranza?.observaciones}
                        color={props.data.detalle_garantia.revision_cobranza?.semaforo_color}>
                    </Semaphore>
                    {((props.data.detalle_garantia.tipo_garantia.codigo === GARANTIA_TIPO.GAR1_CARTA_FIANZA && !props.data.detalle_garantia.is_excepcion) 
                        || (props.data.detalle_garantia.tipo_garantia.codigo === GARANTIA_TIPO.GAR0_EFECTIVO 
                                && props.data.detalle_garantia.excepcion.codigo_tipo_excepcion === EXCEPCION_GARANTIA_TIPO.EFECTIVO_A_CARTA_FIANZA)) ?
                        (<Semaphore caption="TE" size={'25px'} fontSize={'11px'}
                            tooltipTitle={"Revisión de Tesorería"}
                            lastUpdateDate={props.data.detalle_garantia?.revision_tesoreria?.fecha_revision}
                            lastUpdatedBy={props.data.detalle_garantia?.revision_tesoreria?.usuario_revisor}
                            tooltipDescription={props.data.detalle_garantia?.revision_tesoreria?.observaciones}
                            color={props.data.detalle_garantia.revision_tesoreria?.semaforo_color}>
                        </Semaphore>) : null
                    }
                </div>
            ) : <></>
        },
        {
            headerName: 'Editar',
            width: 70,
            filter: false,
            pinned: 'left',
            sortable: false,
            cellRenderer: (props) => {
                if (!props.data) return <></>;
                return (
                    <div className="d-flex align-items-center h-100 justify-content-center link-icon">
                        <button 
                            className="btn btn-link p-0 d-flex align-items-center justify-content-center" 
                            style={{ border: 'none', background: 'transparent' }}
                            onClick={() => handleOpenEdit(props.data.detalle_garantia.id_garantia_detalle)}
                        >
                            <i className="fi fi-rr-pencil cursor-hand color-icon-blue fs-4"></i>
                        </button>
                    </div>
                );
            }
        },
        {
            headerName: 'Contrato',
            field: 'garantia.nro_contrato',
            width: 120,
            filter: 'agTextColumnFilter',
            pinned: 'left',
        },
        {
            headerName: 'Recepcionada?',
            field: 'detalle_garantia.is_recepcionada',
            width: 120,
            cellRenderer: (props) => {
                if (!props.data) return <></>;
                let element = '';
                if (props.data.detalle_garantia.is_recepcionada === true) {
                    element = <Badge bg="" style={{ backgroundColor: props.data.detalle_garantia.recepcion?.semaforo_color }} >Si Recepcionada</Badge>;
                } else if (props.data.detalle_garantia.is_recepcionada === false) {
                    element = <Badge bg="" style={{ backgroundColor: props.data.detalle_garantia.recepcion?.semaforo_color }} >No Recepcionada</Badge>;
                } else {
                    element = <Badge bg="secondary">No Recepcionada</Badge>;
                }
                return <div className="align-items-center d-flex h-100">{element}</div>;
            },
        },
        {
            headerName: 'Renovada?',
            field: 'detalle_garantia.is_renovada',
            width: 100,
            cellRenderer: (props) => {
                if (!props.data) return <></>;
                let element = props.data.detalle_garantia.is_renovada ? <Badge bg="danger">Si Renovada</Badge> : <Badge bg="secondary">No Renovada</Badge>;
                return <div className="align-items-center d-flex h-100">{element}</div>;
            },
        },
        {
            headerName: 'Requerida?',
            field: 'detalle_garantia.is_requerida',
            width: 100,
            cellRenderer: (props) => {
                if (!props.data) return <></>;
                let element = props.data.detalle_garantia.is_requerida ? <Badge bg="danger">Si Requerida</Badge> : <Badge bg="secondary">No Requerida</Badge>;
                return <div className="align-items-center d-flex h-100">{element}</div>;
            },
        },
        {
            headerName: 'Desistida?',
            field: 'detalle_garantia.is_desistida',
            width: 100,
            cellRenderer: (props) => {
                if (!props.data) return <></>;
                let element = props.data.detalle_garantia.is_desistida ? <Badge bg="danger">Si Desistida</Badge> : <Badge bg="secondary">No Desistida</Badge>;
                return <div className="align-items-center d-flex h-100">{element}</div>;
            }
        },
        {
            headerName: 'Ejecutada?',
            field: 'detalle_garantia.is_ejecutada',
            width: 100,
            cellRenderer: (props) => {
                if (!props.data) return <></>;
                let element = props.data.detalle_garantia.is_ejecutada ? <Badge bg="danger">Si Ejecutada</Badge> : <Badge bg="secondary">No Ejecutada</Badge>;
                return <div className="align-items-center d-flex h-100">{element}</div>;
            }
        },
        {
            headerName: 'Excepción?',
            field: 'detalle_garantia.is_excepcion',
            width: 100,
            cellRenderer: (props) => {
                if (!props.data) return <></>;
                let element = props.data.detalle_garantia.is_excepcion ? <Badge bg="danger">Con Excepción</Badge> : <Badge bg="secondary">Sin Excepción</Badge>;
                return <div className="align-items-center d-flex h-100">{element}</div>;
            }
        },
        {
            headerName: 'Datos Generales',
            children: [
                {
                    headerName: 'Origen',
                    field: 'detalle_garantia.origen.nombre',
                    width: 110
                },
                {
                    headerName: 'Estado',
                    field: 'detalle_garantia.estado.nombre',
                    width: 110,
                    cellRenderer: (props) => {
                        if (!props.data) return <></>;
                        let element = '';
                        if (props.data.detalle_garantia.estado.codigo === GARANTIA_ESTADO.RECHAZADO) {
                            element = <Badge bg="danger">{props.data.detalle_garantia.estado.nombre}</Badge>;
                        } else if (props.data.detalle_garantia.estado.codigo === GARANTIA_ESTADO.EN_REVISION) {
                            element = <Badge bg="info">{props.data.detalle_garantia.estado.nombre}</Badge>;
                        } else if (props.data.detalle_garantia.estado.codigo === GARANTIA_ESTADO.APROBADO) {
                            element = <Badge bg="success">{props.data.detalle_garantia.estado.nombre}</Badge>;
                        } else {
                            element = <Badge bg="secondary">{props.data.detalle_garantia.estado.nombre}</Badge>;
                        }
                        return <div className="align-items-center d-flex h-100">{element}</div>;
                    }
                },
                {
                    headerName: 'Tipo',
                    width: 120,
                    field: 'detalle_garantia.tipo_garantia.nombre',
                },
                {
                    headerName: 'RUC',
                    field: 'garantia.ruc',
                    width: 120,
                    filter: 'agTextColumnFilter',
                },
                {
                    headerName: 'Razon Social',
                    field: 'garantia.razon_social',
                    filter: 'agTextColumnFilter',
                },
                {
                    headerName: 'Mall',
                    field: 'garantia.mall.nombre',
                    width: 160,
                },
                {
                    headerName: 'Banco',
                    width: 100,
                    field: 'detalle_garantia.banco.codigo',
                },
                {
                    headerName: 'Importe',
                    field: 'detalle_garantia.importe',
                    type: 'rightAligned',
                    width: 100,
                    filter: 'agTextColumnFilter',
                    valueFormatter: (params) => params.value ? `${numeral(params.value).format('0,0.00')}` : ``,
                },
                {
                    headerName: 'Moneda',
                    field: 'garantia.moneda.codigo',
                    width: 100,
                },
                {
                    headerName: 'Tipo Carta Fianza',
                    field: 'detalle_garantia.descripcion_tipo_carta_fianza',
                    type: 'rightAligned',
                    width: 100,
                },
            ]
        },
        {
            headerName: 'Carta Fianza',
            children: [
                {
                    headerName: 'Nro.Carta Fianza',
                    width: 160,
                    field: 'detalle_garantia.nro_carta_fianza',
                    filter: 'agTextColumnFilter',
                    valueFormatter: (params) => params.value == null ? CELL_CONSTANTS.NO_APLICA : params.value,
                    cellRenderer: (props) => {
                        const cellValue = props.valueFormatted ? props.valueFormatted : props.value;
                        if (cellValue == CELL_CONSTANTS.NO_APLICA) {
                            return <span className='cell-value-not-apply'>{cellValue}</span>;
                        } else {
                            return <a href={props.data.detalle_garantia.url_filename_carta_fianza} className="link-custom-blue d-flex align-items-center mx-2" target="_blank" rel="noreferrer">
                                <span>{props.data.detalle_garantia.nro_carta_fianza}</span>
                            </a>;
                        }
                    }
                },
                {
                    headerName: 'Venc.Carta Fianza',
                    field: 'detalle_garantia.fecha_vencimiento_carta_fianza',
                    filter: 'agDateColumnFilter',
                    filterParams: createCustomDateFilterParams,
                    valueFormatter: (params) => params.value ? `${moment(params.value).format("DD/MM/yyyy")}` : null,
                    width: 180,
                    cellClass: 'date'
                },
            ]
        }
    ], [handleOpenEdit]);

    const agGridLista = useMemo(() => ({
        defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 10,
            floatingFilter: true,
            enableRowGroup: true,
        },
        columnDefs: header,
        rowData: data_revisiones?.data,
        pagination: true,
        paginationAutoPageSize: true,
        titleExcel: 'edicion_garantias_listado',
        rowSelection: 'single',
        domLayout: "normal",
        rowGroupPanelShow: 'always',
        groupDisplayType: 'singleColumn',
        showOpenedGroup: true,
        autoGroupColumnDef: {
            headerName: 'Grupos',
        },
        onGridReady: (params) => {
            gridApiRef.current = params.api;
            columnApiRef.current = params.columnApi;
        },
        getRowId: (params) => {
            return params.data.detalle_garantia.id_garantia_detalle;
        },
    }), [header, data_revisiones?.data, isFetchingDataRevisiones]);

    return (
        <>
            <PermissionsGate RenderError={() => <Unauthorized />} scopes={[SCOPES.GARANTIAS_EDICION]} >
                <PageTitle breadcrumbs={breadcrumbs}>{title}</PageTitle>
                <CardEx title={"Listado de Garantías para Edición"}
                    titleParent={title}
                    height={"100%"}
                    isAvailableMaximized={true}
                    isAvailableBreadcrumbs={true}
                    isAvailableRefetch={true}
                    breadcrumbs={breadcrumbs}
                    onRefetch={async () => {
                        await refetch_data_revisiones();
                        cogoToast.success('Registros actualizados satisfactoriamente!', {
                            heading: 'Listado de Garantías',
                            position: 'top-right'
                        });
                    }} >

                    <div className="d-flex justify-content-end mb-3">
                        <Button
                            variant="primary"
                            className="btn-sm d-flex align-items-center"
                            onClick={() => setShowAddBankModal(true)}
                        >
                            <i className="fi fi-rr-plus me-2 fs-6"></i>
                            Agregar Banco
                        </Button>
                    </div>

                    <WrappedAgTable {...agGridLista} />
                </CardEx>
                
                {showEditModal && (
                    <EditarGarantiaModal
                        showModal={showEditModal}
                        setShowModal={setShowEditModal}
                        idDetalleGarantia={selectedGarantiaId}
                        onSuccess={handleSuccessEdit}
                    />
                )}

                {showAddBankModal && (
                    <AgregarBancoModal
                        showModal={showAddBankModal}
                        setShowModal={setShowAddBankModal}
                        onSuccess={() => {
                            refetch_data_revisiones();
                        }}
                    />
                )}
            </PermissionsGate>
        </>
    );
}
